import Modal from 'bootstrap/js/dist/modal'

export interface ModalOptions {
  modal_id?: string
  backdrop?: boolean | 'static'
  keyboard?: boolean
  focus?: boolean
  size?: string
  centered?: boolean
  scrollable?: boolean
  fullscreen?: boolean
  animate?: boolean
  destroy_on_close?: boolean
}

export function modal_html(content: string, title = 'Modal', w = 920, h = 640, options: ModalOptions = {}): Modal {
  const modal_id = options.modal_id || 'app_modal'
  const backdrop = options.backdrop !== false
  const keyboard = options.keyboard !== false
  const focus = options.focus !== false
  const size = options.size || 'xl'
  const centered = options.centered !== false
  const scrollable = options.scrollable !== false
  const fullscreen = options.fullscreen || false
  const is_dark = document.documentElement.getAttribute('data-bs-theme') === 'dark'
  const theme_class = is_dark ? 'modal-dark' : 'modal-light'
  let dialog_classes = 'modal-dialog'
  if (size && size !== 'default') dialog_classes += ` modal-${size}`
  if (centered) dialog_classes += ' modal-dialog-centered'
  if (scrollable) dialog_classes += ' modal-dialog-scrollable'
  if (fullscreen) dialog_classes += ' modal-fullscreen'
  const existing = document.getElementById(modal_id)
  if (existing) existing.remove()
  const html = `
    <div class="modal fade" id="${modal_id}" tabindex="-1" aria-labelledby="${modal_id}Label" aria-hidden="true">
      <div class="${dialog_classes} ${theme_class}" style="max-width: ${w}px;">
        <div class="modal-content shadow-lg border-0" style="max-height: ${h}px;">
          <div class="modal-header bg-gradient border-0 position-relative">
            <h5 class="modal-title fw-bold d-flex align-items-center" id="${modal_id}Label">
              <i class="fas fa-window-maximize me-2 text-primary"></i>
              <span class="modal-title-text">${title}</span>
            </h5>
            <div class="modal-header-actions d-flex align-items-center">
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar" title="Fechar"></button>
            </div>
          </div>
          <div class="modal-body p-0 position-relative" style="overflow-y: auto;">
            <div class="modal-content-wrapper p-3" id="${modal_id}-body">
              ${content || '<div class="text-center p-4"><i class="fas fa-circle-notch fa-spin fa-2x text-muted"></i><p class="mt-2 text-muted">Carregando...</p></div>'}
            </div>
          </div>
          <div class="modal-footer bg-gradient border-0 d-none" id="${modal_id}-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="fas fa-times me-1"></i>Fechar
            </button>
          </div>
        </div>
      </div>
    </div>`
  document.body.insertAdjacentHTML('beforeend', html)
  const el = document.getElementById(modal_id)
  if (!el) throw new Error(`Modal ${modal_id} não encontrado após inserção no DOM`)
  const instance = new Modal(el, { backdrop, keyboard, focus })
  el.addEventListener('shown.bs.modal', () => {
    const first_input = el.querySelector<HTMLElement>('input, textarea, select')
    if (first_input) first_input.focus()
  })
  el.addEventListener('hidden.bs.modal', () => {
    if (options.destroy_on_close !== false) setTimeout(() => el.remove(), 300)
  })
  instance.show()
  return instance
}

export function modal_confirm(title: string, message: string, confirm_text = 'Sim', confirm_class = 'btn-success'): Promise<boolean> {
  return new Promise(resolve => {
    const modal_id = 'confirm_modal_' + Date.now()
    const content = `
      <div class="text-center p-4">
        <i class="fas fa-question-circle fa-3x text-warning mb-3"></i>
        <h5 class="mb-3">${message}</h5>
        <div class="d-flex justify-content-center gap-3">
          <button type="button" class="btn ${confirm_class}" id="${modal_id}_yes">
            <i class="fas fa-check me-1"></i>${confirm_text}
          </button>
          <button type="button" class="btn btn-danger" id="${modal_id}_no">
            <i class="fas fa-times me-1"></i>Nao
          </button>
        </div>
      </div>`
    modal_html(content, title, 400, 300, { modal_id, size: 'sm', backdrop: 'static', keyboard: false })
    const yes_btn = document.getElementById(`${modal_id}_yes`)
    const no_btn = document.getElementById(`${modal_id}_no`)
    const done = (val: boolean) => { resolve(val); modal_close(modal_id) }
    if (yes_btn) yes_btn.onclick = () => done(true)
    if (no_btn) no_btn.onclick = () => done(false)
  })
}

export function modal_alert(title: string, message: string, type = 'info', on_close?: () => void): void {
  const modal_id = 'alert_modal_' + Date.now()
  const icons: Record<string, string> = {
    success: 'fa-check-circle text-success',
    error: 'fa-exclamation-triangle text-danger',
    warning: 'fa-exclamation-triangle text-warning',
    info: 'fa-info-circle text-info',
  }
  const content = `
    <div class="text-center p-4">
      <i class="fas ${icons[type] || icons.info} fa-3x mb-3"></i>
      <h5 class="mb-3">${message}</h5>
      <button type="button" class="btn btn-primary" id="${modal_id}_ok">
        <i class="fas fa-check me-1"></i>OK
      </button>
    </div>`
  modal_html(content, title, 400, 250, { modal_id, size: 'sm' })
  const ok_btn = document.getElementById(`${modal_id}_ok`)
  if (ok_btn) ok_btn.onclick = () => { if (on_close) on_close(); modal_close(modal_id) }
}

export function modal_prompt(title: string, message: string, default_value = ''): Promise<string | null> {
  return new Promise(resolve => {
    const modal_id = 'prompt_modal_' + Date.now()
    const content = `
      <div class="p-4">
        <div class="mb-3">
          <label class="form-label">${message}</label>
          <input type="text" class="form-control" id="${modal_id}_input" value="${default_value}">
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary" id="${modal_id}_cancel">
            <i class="fas fa-times me-1"></i>Cancelar
          </button>
          <button type="button" class="btn btn-primary" id="${modal_id}_ok">
            <i class="fas fa-check me-1"></i>OK
          </button>
        </div>
      </div>`
    modal_html(content, title, 500, 250, { modal_id, size: 'sm' })
    const input = document.getElementById(`${modal_id}_input`) as HTMLInputElement | null
    const ok_btn = document.getElementById(`${modal_id}_ok`)
    const cancel_btn = document.getElementById(`${modal_id}_cancel`)
    const submit = () => { resolve(input?.value ?? null); modal_close(modal_id) }
    const cancel = () => { resolve(null); modal_close(modal_id) }
    if (ok_btn) ok_btn.onclick = submit
    if (cancel_btn) cancel_btn.onclick = cancel
    if (input) {
      input.onkeypress = (e) => { if (e.key === 'Enter') submit() }
      setTimeout(() => { input.focus(); input.select() }, 300)
    }
  })
}

export function modal_close(id = 'app_modal'): void {
  const el = document.getElementById(id)
  if (!el) return
  const instance = Modal.getInstance(el)
  if (!instance) return
  // Bootstrap ignora hide() durante a animação de abertura.
  const hide_after_open = () => instance.hide()
  el.addEventListener('shown.bs.modal', hide_after_open, { once: true })
  el.addEventListener('hidden.bs.modal', () => el.removeEventListener('shown.bs.modal', hide_after_open), { once: true })
  instance.hide()
}

window.modal_html = modal_html
window.modal_confirm = modal_confirm
window.modal_alert = modal_alert
window.modal_prompt = modal_prompt
window.modal_close = modal_close
